import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "./analytics";
import { AnalyticsEvent } from "./analyticsEvents";
import { getAttributionForAuth } from "./attributionUtm";
import { isTelegramMiniApp } from "./telegram";
import { isMaxMiniApp } from "./max";
/** Первое в истории браузера открытие приложения → acquisition vs retention. */
const FIRST_OPEN_KEY = "tp_analytics_first_open_done";
function readIsFirstOpen() {
    try {
        if (localStorage.getItem(FIRST_OPEN_KEY) === "1") {
            return false;
        }
        localStorage.setItem(FIRST_OPEN_KEY, "1");
        return true;
    }
    catch {
        return false;
    }
}
/**
 * UTM для web_open: сначала из текущего URL, иначе из sessionStorage
 * (после редиректа `/` → `/generate-song` search в адресе мог уже пропасть,
 * а capture в index.tsx успел положить метки в storage).
 */
function readUtmForWebOpen(search) {
    const out = {};
    const keys = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_content",
        "utm_term",
    ];
    try {
        const p = new URLSearchParams(search);
        for (const key of keys) {
            const v = p.get(key);
            if (v) {
                out[key] = v;
            }
        }
    }
    catch {
        /* ignore */
    }
    if (Object.keys(out).length === 0) {
        const stored = getAttributionForAuth();
        for (const key of keys) {
            const v = stored[key];
            if (v) {
                out[key] = v;
            }
        }
    }
    return out;
}
/** entry_point по тому, что можем определить на клиенте. */
function resolveEntryPoint(pathname, search) {
    if (isTelegramMiniApp() || isMaxMiniApp()) {
        return "bot_button";
    }
    // Рекламный заход (в т.ч. после редиректа `/` → `/generate-song`): не
    // помечаем как deeplink — это acquisition, не внутренний диплинк.
    const hasAdsMarks = /(?:^|[?&])(click_id|utm_source|utm_medium|utm_campaign)=/.test(search.startsWith("?") ? search : `?${search}`) || !!getAttributionForAuth().click_id;
    if (hasAdsMarks || !pathname || pathname === "/") {
        return "direct";
    }
    return "deeplink";
}
/**
 * Backbone таймлайна: web_open (один раз на открытие приложения) + page_view на
 * каждый роут. Подключается в App рядом с useServerSiteAnalytics.
 */
export function useAnalyticsPageTracking() {
    const location = useLocation();
    const path = location.pathname;
    const webOpenSentRef = useRef(false);
    const prevPathRef = useRef(null);
    // web_open — один раз за загрузку приложения (воронка, шаг 1).
    useEffect(() => {
        if (webOpenSentRef.current) {
            return;
        }
        webOpenSentRef.current = true;
        const search = window.location.search;
        const attribution = getAttributionForAuth();
        trackEvent(AnalyticsEvent.WEB_OPEN, {
            entry_point: resolveEntryPoint(window.location.pathname, search),
            is_first_open: readIsFirstOpen(),
            ...readUtmForWebOpen(search),
            ...(attribution.click_id ? { click_id: attribution.click_id } : {}),
            ...(document.referrer ? { referrer: document.referrer } : {}),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // page_view — на каждый роут, с from_path (предыдущий путь).
    useEffect(() => {
        const from = prevPathRef.current;
        trackEvent(AnalyticsEvent.PAGE_VIEW, {
            path,
            title: typeof document !== "undefined" ? document.title : "",
            ...(from && from !== path ? { from_path: from } : {}),
        });
        prevPathRef.current = path;
    }, [path]);
}
